import type { Icon } from '@/assets/icons'
import { formatNumberToLocale } from '@/composables/useFormatNumberToLocale'
import { render } from 'vike/abort'
import type {
  UiProposalProps,
  UiProposalVariant,
} from '@afs/design-system/components/molecules/UiProposal/interface.d.ts'
import type { UiHelpProps } from '@afs/design-system/components/organisms/UiHelp/interface.d.ts'

import { mapFaqAccordionQuestion } from '~/extensions/question/question.mapper'
import type {
  SimulationProposalType,
  SimulatorSubProjectType,
  SimulatorType,
} from '~/extensions/simulator/simulator.type'
import { mapImage } from '~/extensions/utils/common/images/image.mapper'
import { mapLink } from '~/extensions/utils/common/link/link.mapper'
import { mapPhoneBlock } from '~/extensions/utils/common/phoneBlock/phoneBlock.mapper'
import { richTextConvertorToHtml } from '~/extensions/utils/common/richText/richText.helper'
import { mapStepBlock } from '~/extensions/utils/common/stepBlock/stepsBlock.mapper'

export function mapSimulator(data: SimulatorType) {
  const popin = data.fields.pop_in?.fields
  const logo = data.fields.logo?.fields
  return {
    stepsBlock: data.fields.steps_block ? mapStepBlock(data.fields.steps_block) : undefined,
    blockFaq: mapFaqBlock(data),
    faqList: mapFaqAccordionQuestion(data.fields.block_faq_questions),
    popin: {
      title: popin?.title,
      subtitle: popin?.subtitle,
      content: popin?.content ? richTextConvertorToHtml(popin.content) : undefined,
      picture: mapImage(popin?.picture),
      link: popin?.link ? mapLink(popin.link) : undefined,
      phoneNumber: popin?.phone_number,
      caption: popin?.call_cost,
    },
    logo: mapImage(logo?.logo_meia),
    linkLogo: logo?.link ? mapImage(logo?.link) : undefined,
    partnerLogo: logo?.logo_partner ? mapImage(logo?.logo_partner) : undefined,
  }
}

export function mapFaqBlock(data: SimulatorType): UiHelpProps {
  return {
    title: data.fields.block_faq_title,
    contact: data.fields.block_phone ? mapPhoneBlock(data.fields.block_phone) : undefined,
  }
}

export function mapSimulatorSubProjectCard(data: SimulatorSubProjectType) {
  try {
    return {
      id: data.sys.id,
      label: data.fields.label,
      projectId: data.fields.simulator_project?.sys.id,
      project: data.fields.simulator_project
        ? {
            id: data.fields.simulator_project.sys.id,
            label: data.fields.simulator_project.fields.label,
            projectType: data.fields.simulator_project.fields.project_type as string,
            icon: data.fields.simulator_project.fields.icon as Icon,
          }
        : undefined,
      projectType: data.fields.project_type,
      icon: data.fields.icon,
    }
  } catch {
    throw render(500)
  }
}

export function mapSimulationProposal(
  proposal: SimulationProposalType,
  variant: UiProposalVariant,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  t: (key: string, options?: Record<string, any>) => string,
): UiProposalProps {
  const hasInsurances = !proposal.borrowers.every((borrower) => borrower.monthlyAmount === undefined)
  return {
    id: proposal.id,
    title:
      proposal.id === 'variation-DURATION-6'
        ? t('simulator.proposals.title.comfort')
        : proposal.id === 'variation-DURATION--6'
          ? t('simulator.proposals.title.fast')
          : t('simulator.proposals.title.main'),
    offer: {
      label: t('simulator.proposals.offer.label'),
      value: formatNumberToLocale(proposal.monthlyPaymentNoInsurance),
      append: t('simulator.proposals.offer.append'),
      caption: t('simulator.proposals.offer.caption', { duration: proposal.numberOfPayments }),
      underCaption: t('simulator.proposals.offer.underCaption'),
    },
    description: t('simulator.proposals.description'),
    underDescription: t('simulator.proposals.underDescription'),
    details: [
      {
        id: 'annualPercentageRateRate',
        label: t('simulator.proposals.details.annualPercentageRateRate.label'),
        value: `${formatNumberToLocale(proposal.annualPercentageRate)} %`,
        labelCaption: t('simulator.proposals.details.annualPercentageRateRate.labelCaption'),
      },
      {
        id: 'purchaseAmount',
        label: t('simulator.proposals.details.purchaseAmount.label'),
        value: `${formatNumberToLocale(proposal.purchaseAmount)} €`,
      },
      {
        id: 'nominalRate',
        label: t('simulator.proposals.details.nominalRate.label'),
        value: `${formatNumberToLocale(proposal.nominalRate)} %`,
      },
      {
        id: 'numberOfPayments',
        label: t('simulator.proposals.details.numberOfPayments.label'),
        value: `${formatNumberToLocale(proposal.numberOfPayments)} mois`,
        valueCaption: `(${formatNumberToLocale(proposal.numberOfPayments / 12)} ${t('simulator.proposals.details.numberOfPayments.valueCaption')})`,
      },
      {
        id: 'fees',
        label: t('simulator.proposals.details.fees.label'),
        value: `${formatNumberToLocale(proposal.fees)}`,
      },
      {
        id: 'totalAmountNoInsurance',
        label: t('simulator.proposals.details.totalAmountNoInsurance.label'),
        value: `${formatNumberToLocale(proposal.totalAmountNoInsurance)} €`,
      },
    ],
    variant,
    insuranceTitle: hasInsurances ? mapInsuranceProposal(proposal).title : undefined,
    insurance: hasInsurances ? mapInsuranceProposal(proposal).details : undefined,
  }
}

function mapInsuranceProposal(proposal: SimulationProposalType) {
  return {
    title: 'Avec assurance emprunteur',
    details: [
      {
        id: 'monthlyPaymentInsurance',
        label: "Coût de l'assurance",
        value: `${formatNumberToLocale(proposal.monthlyPaymentWithInsurance - proposal.monthlyPaymentNoInsurance)} €`,
        labelCaption: '(par mois)',
      },
      {
        id: 'monthlyPaymentWithInsurance',
        label: 'Mensualité avec assurance',
        value: `${formatNumberToLocale(proposal.monthlyPaymentWithInsurance)} €`,
      },
      {
        id: 'insuranceAnnualPercentageRate',
        label: 'TAEA',
        value: `${formatNumberToLocale(proposal.insuranceAnnualPercentageRate)} %`,
        labelCaption: "(taux annuel effectif de l'assurance)",
      },
      {
        id: 'totalAmountInsurance',
        label: "Montant dû au titre de l'assurance",
        value: `${formatNumberToLocale(proposal.totalAmountWithInsurance - proposal.totalAmountNoInsurance)} €`,
      },
    ],
  }
}
